import React, { Suspense, lazy, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import markLogo from 'src/assets/branding/auto-appy-mark-256x256.png';
import { useAuth } from 'src/context/AuthContext';
import GbpSidebar from 'src/stage/GbpSidebar';
import { Skeleton } from 'src/stage/StageUi';
import { WorkspaceProvider, useWorkspace } from 'src/workspace/WorkspaceContext';
import SignInPage from 'src/features/Auth/SignInPage';

// Every page loads on demand so the first screen stays light.
const page = (load) => lazy(load);
const HomePage = page(() => import('src/features/Home/HomePage'));
const ReviewsPage = page(() => import('src/features/Reviews/ReviewsPage'));
const AutoReplyPage = page(() => import('src/features/AutoReply/AutoReplyPage'));
const PostsPage = page(() => import('src/features/Posts/PostsPage'));
const PostEditorPage = page(() => import('src/features/Posts/PostEditorPage'));
const ProfilePage = page(() => import('src/features/Profile/ProfilePage'));
const InsightsPage = page(() => import('src/features/Insights/InsightsPage'));
const SeoPage = page(() => import('src/features/Seo/SeoPage'));
const GetReviewsPage = page(() => import('src/features/GetReviews/GetReviewsPage'));
const ActivityPage = page(() => import('src/features/Activity/ActivityPage'));
const SettingsPage = page(() => import('src/features/Settings/SettingsPage'));
const GuidePage = page(() => import('src/features/Guide/GuidePage'));
const LegalPage = page(() => import('src/features/Legal/LegalPage'));

export function Boot({ title = 'Opening Google My Business…', children }) {
  return (
    <div className="mx-boot" role="status">
      <img src={markLogo} alt="" />
      <strong>{title}</strong>
      {children || <span className="mx-boot-bar" aria-hidden="true" />}
    </div>
  );
}

function RouteLoading() {
  return (
    <main className="ws mx-stage" aria-busy="true">
      <div className="ws-page">
        <Skeleton width={160} height={14} />
        <Skeleton width="42%" height={34} style={{ marginTop: 6 }} />
        <Skeleton height={220} radius={20} style={{ marginTop: 12 }} />
      </div>
    </main>
  );
}

function Shell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { organisations, bootError, boot } = useWorkspace();
  if (!organisations.length) {
    return (
      <Boot title="You are not in an organisation yet">
        <span>Google My Business works inside an Auto Appy organisation. Ask your team owner to invite you, or create an organisation in Auto Appy.</span>
        <a className="ws-btn ws-btn--primary" href={`${process.env.REACT_APP_AUTOAPPY_MAIN_URL || 'https://autoappy.com'}/organisation`}>Open Auto Appy</a>
      </Boot>
    );
  }
  return (
    <div className="mx-shell">
      <GbpSidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <div className="mx-main">
        {bootError && !boot ? (
          <Boot title="Google My Business could not load">
            <span>{bootError}</span>
            <button type="button" className="ws-btn ws-btn--primary" onClick={() => window.location.reload()}>Try again</button>
          </Boot>
        ) : !boot ? <RouteLoading /> : <Suspense fallback={<RouteLoading />}>{children}</Suspense>}
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading, error, signIn } = useAuth();
  const location = useLocation();
  if (loading) return <Boot />;
  if (error === 'access_denied') {
    return (
      <Boot title="No access to Google My Business">
        <span>Your Auto Appy account does not have Google My Business turned on. Ask an organisation owner to give you access.</span>
      </Boot>
    );
  }
  if (!user) return <SignInPage onSignIn={() => signIn(`${location.pathname}${location.search}`)} offline={error === 'offline'} />;
  return <WorkspaceProvider><Shell>{children}</Shell></WorkspaceProvider>;
}

export default function AppRoutes() {
  const location = useLocation();
  const failed = new URLSearchParams(location.search).get('reason');
  return (
    <Routes>
      <Route path="/auth/error" element={(
        <Boot title="Sign-in did not finish">
          <span>{failed === 'access_denied' ? 'Your account does not have access to Google My Business yet.' : 'Please try signing in again.'}</span>
          <a className="ws-btn ws-btn--primary" href="/home">Try again</a>
        </Boot>
      )} />
      <Route path="/" element={<Navigate replace to={`/home${location.search}`} />} />
      {['privacy', 'terms', 'data-deletion'].map((kind) => (
        <Route key={kind} path={`/${kind}`} element={<Suspense fallback={<Boot title="Loading…" />}><LegalPage kind={kind} /></Suspense>} />
      ))}
      <Route path="/home" element={<Protected><HomePage /></Protected>} />
      <Route path="/reviews" element={<Protected><ReviewsPage /></Protected>} />
      <Route path="/auto-reply" element={<Protected><AutoReplyPage /></Protected>} />
      <Route path="/posts" element={<Protected><PostsPage /></Protected>} />
      <Route path="/posts/new" element={<Protected><PostEditorPage /></Protected>} />
      <Route path="/posts/:postId" element={<Protected><PostEditorPage /></Protected>} />
      <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
      <Route path="/insights" element={<Protected><InsightsPage /></Protected>} />
      <Route path="/seo" element={<Protected><SeoPage /></Protected>} />
      <Route path="/get-reviews" element={<Protected><GetReviewsPage /></Protected>} />
      <Route path="/activity" element={<Protected><ActivityPage /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/settings/:section" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/guide" element={<Protected><GuidePage /></Protected>} />
      <Route path="*" element={<Protected><Navigate replace to="/home" /></Protected>} />
    </Routes>
  );
}
